import { UserInputError } from "./errors-CEDWDXew.js";
import { resolveScopedOrganizationSlug } from "./url-scope-CRM5sNo7.js";
import { agentTool, recordAgentToolResultCount } from "./utils-DHwF0RI3.js";
import { EXAMPLE_QUERIES, ISSUE_EVENT_TAGS, RECOMMENDED_FIELDS } from "./config-ChOUIglS.js";
import { z } from "zod";

//#region src/tools/support/search-issue-events/utils.ts
/**
* Create a tool for the agent to query available tags and fields for issue events
* The tool is pre-bound with the API service and organization configured for the appropriate region
*/
function createIssueEventFieldsTool(options) {
	const { apiService, organizationSlug, projectId } = options;
	return agentTool({
		description: "Get available tags and fields for issue events to understand what filters are available",
		parameters: z.object({}),
		execute: async () => {
			const tagsResponse = await apiService.listTags({
				organizationSlug,
				dataset: "events",
				project: projectId,
				statsPeriod: "14d",
				useCache: true,
				useFlagsBackend: true
			});
			const availableTags = {};
			for (const tag of tagsResponse) if (tag.key) {
				const knownTagDescription = ISSUE_EVENT_TAGS[tag.key];
				availableTags[tag.key] = tag.name || knownTagDescription || tag.key;
			}
			const allTags = {
				...ISSUE_EVENT_TAGS,
				...availableTags
			};
			const tagCount = Object.keys(allTags).length;
			recordAgentToolResultCount(tagCount);
			return `Available Tags and Fields (${tagCount} total):

Common Tags:
${Object.entries(ISSUE_EVENT_TAGS).slice(0, 30).map(([key, desc]) => `- ${key}: ${desc}`).join("\n")}
${Object.keys(ISSUE_EVENT_TAGS).length > 30 ? `\n... and ${Object.keys(ISSUE_EVENT_TAGS).length - 30} more common tags` : ""}

Project-Specific Tags (from API):
${Object.entries(availableTags).filter(([key]) => !ISSUE_EVENT_TAGS[key]).slice(0, 20).map(([key, desc]) => `- ${key}: ${desc}`).join("\n")}

Recommended Fields for Results:
${RECOMMENDED_FIELDS.map((f) => `- ${f}`).join("\n")}

EXAMPLE QUERIES:
${EXAMPLE_QUERIES.map((ex) => `- "${ex.description}" →\n  ${JSON.stringify(ex.output, null, 2)}`).join("\n\n")}

IMPORTANT:
- Use these tag names exactly as shown in your query
- Tags are case-insensitive
- Use wildcards with quotes: url:"*/checkout/*"
- Your query should NOT include "issue:" prefix - the handler adds it automatically`;
		}
	});
}
/**
* Parse issue parameters from various input formats
* Supports both direct issueId/organizationSlug and issueUrl parsing
*/
function parseIssueParams(params) {
	if (params.issueUrl) {
		const orgMatch = params.issueUrl.match(/\/organizations\/([^/]+)\/issues\/([^/?]+)/) || params.issueUrl.match(/https?:\/\/([^.]+)\.sentry\.io\/issues\/([^/?]+)/);
		if (!orgMatch) throw new UserInputError(`Invalid issue URL format. Expected format like:
- https://sentry.io/organizations/my-org/issues/123/
- https://my-org.sentry.io/issues/123/

Received: ${params.issueUrl}`);
		return {
			organizationSlug: resolveScopedOrganizationSlug({
				resourceLabel: "Issue",
				scopedOrganizationSlug: params.organizationSlug,
				urlOrganizationSlug: orgMatch[1]
			}),
			issueId: orgMatch[2]
		};
	}
	if (!params.issueId) throw new UserInputError("Must provide either issueUrl or issueId parameter. Use issueUrl for convenience, or provide both issueId and organizationSlug.");
	if (!params.organizationSlug) throw new UserInputError("When using issueId parameter, organizationSlug is required. Alternatively, provide issueUrl which includes both organization and issue ID.");
	return {
		organizationSlug: params.organizationSlug,
		issueId: params.issueId
	};
}

//#endregion
export { createIssueEventFieldsTool, parseIssueParams };
//# sourceMappingURL=utils-_4r1K2PE.js.map